import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import WorkoutChart from './WorkoutChart';
import WorkoutFolderTree, { FolderIcon } from './WorkoutFolderTree';
import WorkoutSharedBy from './WorkoutSharedBy';
import TrackPreview from './TrackPreview';
import ActivityStats from './ActivityStats';
import AppSyncPanel from './AppSyncPanel';
// Официальный логотип с mywhoosh.com (SVG у MyWhoosh нет), без слогана; белый —
// для темной темы. Zwift — словесный знак из футера zwift.com
import mywhooshLogo from '../mywhoosh-logo.png';
import mywhooshLogoWhite from '../mywhoosh-logo-white.png';
import zwiftLogo from '../zwift-logo.svg';
import {
  WORKOUT_FILE_ACCEPT,
  formatDuration,
  workoutFileFormat,
  workoutMetrics,
} from '../utils/workouts';
import {
  DRAG_FOLDER,
  DRAG_WORKOUT,
  buildFolderTree,
  descendantIds,
  folderOptions,
  folderPath,
  folderUrl,
} from '../utils/workoutFolders';
import { activityBadge, formatDate, formatDistance } from '../utils/activities';
import './WorkoutsPage.css';

// Раздел «Мои тренировки»: дерево папок, загрузка файлов в текущую папку — планов
// ERG/MRC/ZWO и выполненных тренировок GPX/FIT/TCX (файл с GPS-треком бэкенд
// кладет в папку Outdoor) и список тренировок папки. Подробности — в WorkoutView.
// Открытая папка живет в адресе (?folder=<id>), чтобы на нее можно было вернуться.
// Заезды подключенных MyWhoosh и Zwift бэкенд сам кладет в их корневые папки
const OUTDOOR_FOLDER = 'outdoor';
const MYWHOOSH_FOLDER = 'mywhoosh';
const ZWIFT_FOLDER = 'zwift';

const MYWHOOSH_LOGO = (
  <>
    <img src={mywhooshLogo} alt="" className="app-sync-logo-light" />
    <img src={mywhooshLogoWhite} alt="" className="app-sync-logo-dark" />
  </>
);
const ZWIFT_LOGO = <img src={zwiftLogo} alt="" />;

const WorkoutsPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef(null);
  const dragRef = useRef(null);

  const [folders, setFolders] = useState([]);
  const [rootCount, setRootCount] = useState(0);
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [workouts, setWorkouts] = useState([]);
  // Меняется, когда тренировки добавились не из этой страницы (синхронизация MyWhoosh, Zwift)
  const [reloadKey, setReloadKey] = useState(0);
  const loadedFolderRef = useRef();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);

  // create | rename | move — открытая форма действия с папкой
  const [mode, setMode] = useState(null);
  const [draft, setDraft] = useState('');
  const [treeOpen, setTreeOpen] = useState(false);
  const [dropTarget, setDropTarget] = useState(undefined);

  // Очередь загрузки (FIFO): { key, file, status: pending|uploading|done|error, error }.
  // Копия в ref нужна циклу загрузки: он берет голову очереди, а файлы,
  // добавленные во время загрузки, встают в хвост и уходят следом
  const [queue, setQueue] = useState([]);
  const queueRef = useRef([]);
  const queueKey = useRef(0);
  const uploadingRef = useRef(false);
  const [ftp, setFtp] = useState(user?.ftp ? String(user.ftp) : '');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Сколько файлов с GPS-треком бэкенд положил не в выбранную папку, а в Outdoor
  const [movedNotice, setMovedNotice] = useState(null);

  const tree = useMemo(() => buildFolderTree(folders, rootCount), [folders, rootCount]);
  const folderParam = Number(searchParams.get('folder')) || null;
  // Папку из адреса могли удалить — тогда показываем корень
  const currentId = foldersLoaded && folderParam && !tree.byId.has(folderParam) ? null : folderParam;
  const current = currentId ? tree.byId.get(currentId) : null;
  const path = folderPath(tree, currentId);
  const subfolders = tree.children.get(currentId) || [];
  // Корневая Outdoor (имя без учета регистра, как ищет бэкенд) и ее подпапки —
  // выполненные тренировки на улице, над ними общая статистика
  const inOutdoor = path[0]?.name.toLowerCase() === OUTDOOR_FOLDER;
  const inMyWhoosh = path[0]?.name.toLowerCase() === MYWHOOSH_FOLDER;
  const inZwift = path[0]?.name.toLowerCase() === ZWIFT_FOLDER;
  // Подключение открыто не всем — флаги users.mywhoosh_access / zwift_access.
  // Папки с уже забранными заездами и статистика по ним остаются в любом случае
  const showMyWhoosh = Boolean(user?.mywhoosh_access) && (!current || inMyWhoosh);
  const showZwift = Boolean(user?.zwift_access) && (!current || inZwift);
  // Папка, открытая сейчас: загрузка могла начаться в другой
  const currentIdRef = useRef(currentId);
  currentIdRef.current = currentId;

  const loadFolders = useCallback(async () => {
    const res = await axios.get('/api/workout_folders');
    setFolders(res.data.folders);
    setRootCount(res.data.root_workouts_count);
    setFoldersLoaded(true);
  }, []);

  useEffect(() => {
    loadFolders().catch((err) => setLoadError(err.response?.data?.error || t('workouts.loadFailed')));
  }, [loadFolders, t]);

  useEffect(() => {
    let cancelled = false;
    // Перечитывание той же папки после синхронизации — без «Загрузка…» на месте списка
    if (loadedFolderRef.current !== currentId) setLoading(true);
    loadedFolderRef.current = currentId;
    axios.get('/api/workouts', { params: { folder_id: currentId ?? 'root' } })
      .then((res) => { if (!cancelled) setWorkouts(res.data.workouts); })
      .catch((err) => { if (!cancelled) setLoadError(err.response?.data?.error || t('workouts.loadFailed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentId, reloadKey, t]);

  const handleImported = useCallback(() => {
    loadFolders().catch(() => {});
    setReloadKey((key) => key + 1);
  }, [loadFolders]);

  const selectFolder = (id) => {
    setSearchParams(id ? { folder: String(id) } : {});
    setMode(null);
    setActionError('');
    setTreeOpen(false);
  };

  const runAction = async (action) => {
    setBusy(true);
    setActionError('');
    try {
      await action();
    } catch (err) {
      setActionError(err.response?.data?.error || t('workouts.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const openMode = (next) => {
    setMode(mode === next ? null : next);
    setActionError('');
    if (next === 'create') setDraft('');
    if (next === 'rename') setDraft(current?.name || '');
    if (next === 'move') setDraft(current?.parent_id ? String(current.parent_id) : '');
  };

  const submitFolderForm = (event) => {
    event.preventDefault();
    runAction(async () => {
      if (mode === 'create') {
        const res = await axios.post('/api/workout_folders', { name: draft.trim(), parent_id: currentId });
        await loadFolders();
        selectFolder(res.data.id);
      } else if (mode === 'rename') {
        await axios.patch(`/api/workout_folders/${currentId}`, { name: draft.trim() });
        await loadFolders();
      } else if (mode === 'move') {
        await axios.patch(`/api/workout_folders/${currentId}`, { parent_id: draft ? Number(draft) : null });
        await loadFolders();
      }
      setMode(null);
    });
  };

  const deleteFolder = () => {
    const parentName = current.parent_id ? tree.byId.get(current.parent_id)?.name : t('workouts.folders.root');
    const question = t('workouts.folders.deleteConfirm')
      .replace('{{name}}', current.name)
      .replace('{{target}}', parentName);
    if (!window.confirm(question)) return;

    runAction(async () => {
      await axios.delete(`/api/workout_folders/${currentId}`);
      await loadFolders();
      selectFolder(current.parent_id);
    });
  };

  const moveWorkout = (workoutId, targetId) => runAction(async () => {
    await axios.patch(`/api/workouts/${workoutId}`, { folder_id: targetId });
    setWorkouts((prev) => prev.filter((workout) => workout.id !== workoutId));
    await loadFolders();
  });

  const moveFolder = (folderId, targetId) => runAction(async () => {
    await axios.patch(`/api/workout_folders/${folderId}`, { parent_id: targetId });
    await loadFolders();
  });

  // Перетаскивание: тренировку — в любую другую папку, папку — куда угодно,
  // кроме нее самой и ее подпапок. Что тащат, помним в ref: в dragover данные
  // из dataTransfer браузер не отдает
  const canDrop = (targetId) => {
    const drag = dragRef.current;
    if (!drag) return false;
    if (drag.type === 'workout') return targetId !== currentId;
    const folder = tree.byId.get(drag.id);
    if (!folder || drag.id === targetId || descendantIds(tree, drag.id).has(targetId)) return false;
    return (folder.parent_id ?? null) !== targetId;
  };

  const endDrag = () => {
    dragRef.current = null;
    setDropTarget(undefined);
  };

  const dnd = {
    target: dropTarget,
    canDrop,
    end: endDrag,
    startFolder: (event, id) => {
      if (id === null) return;
      event.stopPropagation();
      dragRef.current = { type: 'folder', id };
      event.dataTransfer.setData(DRAG_FOLDER, String(id));
      event.dataTransfer.effectAllowed = 'move';
    },
    dropProps: (targetId) => ({
      onDragOver: (event) => {
        if (!canDrop(targetId)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (dropTarget !== targetId) setDropTarget(targetId);
      },
      onDragLeave: (event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setDropTarget((prev) => (prev === targetId ? undefined : prev));
      },
      onDrop: (event) => {
        event.preventDefault();
        const drag = dragRef.current;
        const allowed = canDrop(targetId);
        endDrag();
        if (!drag || !allowed) return;
        if (drag.type === 'workout') moveWorkout(drag.id, targetId);
        else moveFolder(drag.id, targetId);
      },
    }),
  };

  const startWorkoutDrag = (event, workout) => {
    dragRef.current = { type: 'workout', id: workout.id };
    event.dataTransfer.setData(DRAG_WORKOUT, String(workout.id));
    event.dataTransfer.effectAllowed = 'move';
  };

  // ── Загрузка файлов в текущую папку ────────────────────────────────────────

  const updateQueue = (change) => {
    queueRef.current = change(queueRef.current);
    setQueue(queueRef.current);
  };

  const setItem = (key, patch) => updateQueue((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  // Новые файлы — в хвост очереди, в том порядке, в каком их отдал браузер.
  // Тот же файл, уже ждущий в очереди, второй раз не добавляем
  const pickFiles = (fileList) => {
    const sameFile = (a, b) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
    const picked = Array.from(fileList || []).filter((file) => (
      !queueRef.current.some((item) => item.status === 'pending' && sameFile(item.file, file))
    ));

    updateQueue((prev) => [...prev, ...picked.map((file) => {
      const supported = Boolean(workoutFileFormat(file.name));
      return {
        key: queueKey.current++,
        file,
        status: supported ? 'pending' : 'error',
        error: supported ? '' : t('workouts.upload.unsupported'),
      };
    })]);
    // Иначе повторный выбор того же файла не вызовет onChange
    if (inputRef.current) inputRef.current.value = '';
  };

  const pendingCount = queue.filter((item) => item.status === 'pending').length;
  const hasErg = queue.some((item) => item.status === 'pending' && workoutFileFormat(item.file.name) === 'erg');

  const handleUpload = async () => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setUploading(true);
    const folderId = currentId;
    let created = 0;
    let moved = null;
    setMovedNotice(null);

    // FIFO: всегда первый ожидающий; ошибка в одном файле не останавливает остальные
    for (;;) {
      const item = queueRef.current.find((entry) => entry.status === 'pending');
      if (!item) break;

      setItem(item.key, { status: 'uploading' });
      const form = new FormData();
      form.append('file', item.file);
      if (folderId) form.append('folder_id', folderId);
      if (workoutFileFormat(item.file.name) === 'erg' && ftp) form.append('ftp', ftp);

      try {
        const res = await axios.post('/api/workouts', form);
        created += 1;
        setItem(item.key, { status: 'done' });
        const landedId = res.data.folder_id ?? null;
        if (landedId !== folderId) moved = { folderId: landedId, count: (moved?.count || 0) + 1 };
        // Список идет в порядке загрузки — новая тренировка встает в конец
        if (currentIdRef.current === landedId) setWorkouts((prev) => [...prev, res.data]);
      } catch (err) {
        setItem(item.key, { status: 'error', error: err.response?.data?.error || t('workouts.upload.failed') });
      }
    }

    // Загруженные уже видны в списке; в очереди остаются только ошибки
    updateQueue((prev) => prev.filter((item) => item.status !== 'done'));
    uploadingRef.current = false;
    setUploading(false);
    setMovedNotice(moved);
    if (created > 0) loadFolders().catch(() => {});
  };

  const removeFromQueue = (key) => updateQueue((prev) => prev.filter((item) => item.key !== key));
  const clearQueue = () => updateQueue((prev) => prev.filter((item) => item.status === 'uploading'));

  const queueStatus = (item) => {
    if (item.status === 'pending') return t('workouts.upload.queued');
    if (item.status === 'uploading') return t('workouts.upload.uploadingFile');
    if (item.status === 'done') return t('workouts.upload.done');
    return item.error;
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    pickFiles(event.dataTransfer.files);
  };

  const isFileDrag = (event) => Array.from(event.dataTransfer.types || []).includes('Files');

  const moveOptions = current ? folderOptions(tree, current.id) : [];
  const empty = !loading && workouts.length === 0 && subfolders.length === 0;

  return (
    <div className="workouts-page workouts-page--wide">
      <div className="workouts-header">
        <h2>{t('workouts.title')}</h2>
        <p className="workouts-subtitle">{t('workouts.subtitle')}</p>
      </div>

      <button
        type="button"
        className="workouts-secondary-btn workouts-tree-toggle"
        onClick={() => setTreeOpen(!treeOpen)}
        aria-expanded={treeOpen}
      >
        {treeOpen ? t('workouts.folders.hideTree') : t('workouts.folders.showTree')}
      </button>

      <div className="workouts-layout">
        <aside className={`workouts-sidebar ${treeOpen ? 'workouts-sidebar--open' : ''}`}>
          <div className="workouts-sidebar-title">{t('workouts.folders.title')}</div>
          <WorkoutFolderTree tree={tree} currentId={currentId} onSelect={selectFolder} dnd={dnd} />
          <p className="workouts-muted wf-dnd-hint">{t('workouts.folders.dndHint')}</p>
        </aside>

        <div className="workouts-main">
          <nav className="wf-breadcrumbs" aria-label={t('workouts.folders.path')}>
            {[null, ...path.map((folder) => folder.id)].map((id, index, ids) => {
              const label = id === null ? t('workouts.folders.root') : tree.byId.get(id).name;
              const last = index === ids.length - 1;
              return (
                <React.Fragment key={id ?? 'root'}>
                  {index > 0 && <span className="wf-breadcrumbs-sep">/</span>}
                  {last ? (
                    <span className="wf-breadcrumbs-current">{label}</span>
                  ) : (
                    <Link
                      to={folderUrl(id)}
                      className={`wf-breadcrumbs-link ${dropTarget === id && canDrop(id) ? 'wf-node--drop' : ''}`}
                      onClick={() => { setMode(null); setActionError(''); }}
                      {...dnd.dropProps(id)}
                    >
                      {label}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          <div className="wf-toolbar">
            <button type="button" className="workouts-secondary-btn" onClick={() => openMode('create')} disabled={busy}>
              + {t('workouts.folders.create')}
            </button>
            {current && (
              <>
                <button type="button" className="workouts-secondary-btn" onClick={() => openMode('rename')} disabled={busy}>
                  {t('workouts.folders.rename')}
                </button>
                <button type="button" className="workouts-secondary-btn" onClick={() => openMode('move')} disabled={busy}>
                  {t('workouts.folders.move')}
                </button>
                <button type="button" className="workouts-danger-btn" onClick={deleteFolder} disabled={busy}>
                  {t('workouts.folders.delete')}
                </button>
              </>
            )}
          </div>

          {mode && (
            <form className="wf-form" onSubmit={submitFolderForm}>
              <label htmlFor="wf-form-field">
                {mode === 'create' && t('workouts.folders.newName')}
                {mode === 'rename' && t('workouts.folders.name')}
                {mode === 'move' && t('workouts.folders.moveTo')}
              </label>
              <div className="wf-form-row">
                {mode === 'move' ? (
                  <select
                    id="wf-form-field"
                    className="workouts-input wf-form-input"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                  >
                    <option value="">{t('workouts.folders.root')}</option>
                    {moveOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="wf-form-field"
                    className="workouts-input wf-form-input"
                    value={draft}
                    maxLength={100}
                    onChange={(event) => setDraft(event.target.value)}
                    autoFocus
                  />
                )}
                <button
                  type="submit"
                  className="workouts-primary-btn"
                  disabled={busy || (mode !== 'move' && !draft.trim())}
                >
                  {mode === 'create' ? t('common.create') : t('common.save')}
                </button>
                <button type="button" className="workouts-secondary-btn" onClick={() => setMode(null)} disabled={busy}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}

          {actionError && <p className="workouts-errors">{actionError}</p>}

          {(showMyWhoosh || showZwift) && (
            <div className="app-sync-panels">
              {showMyWhoosh && (
                <AppSyncPanel app="mywhoosh" name="MyWhoosh" logo={MYWHOOSH_LOGO} onImported={handleImported} />
              )}
              {showZwift && (
                <AppSyncPanel app="zwift" name="Zwift" logo={ZWIFT_LOGO} onImported={handleImported} />
              )}
            </div>
          )}

          {(inOutdoor || inMyWhoosh || inZwift) && <ActivityStats folderId={currentId} reloadKey={folders} />}

          <section className="workouts-upload">
            <label
              className={`workouts-dropzone ${dragging ? 'workouts-dropzone--active' : ''}`}
              onDragOver={(event) => { if (isFileDrag(event)) { event.preventDefault(); setDragging(true); } }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleFileDrop}
            >
              <input
                ref={inputRef}
                type="file"
                accept={WORKOUT_FILE_ACCEPT}
                multiple
                onChange={(event) => pickFiles(event.target.files)}
              />
              <span className="workouts-dropzone-title">{t('workouts.upload.drop')}</span>
              <span className="workouts-dropzone-hint">
                {t('workouts.upload.formats')}
                {' · '}
                {t('workouts.upload.target').replace('{{folder}}', current ? current.name : t('workouts.folders.root'))}
              </span>
            </label>

            {hasErg && (
              <div className="workouts-ftp-field">
                <label htmlFor="workouts-ftp">{t('workouts.upload.ftpLabel')}</label>
                <div className="workouts-ftp-input">
                  <input
                    id="workouts-ftp"
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={ftp}
                    onChange={(event) => setFtp(event.target.value)}
                  />
                  <span>{t('workouts.units.watts')}</span>
                </div>
                <p className="workouts-field-hint">{t('workouts.upload.ftpHint')}</p>
              </div>
            )}

            {queue.length > 0 && (
              <ol className="workouts-queue" aria-label={t('workouts.upload.queueTitle')}>
                {queue.map((item, index) => (
                  <li key={item.key} className={`workouts-queue-item workouts-queue-item--${item.status}`}>
                    <span className="workouts-queue-num">{index + 1}</span>
                    <span className="workouts-queue-name">{item.file.name}</span>
                    <span className="workouts-queue-status">{queueStatus(item)}</span>
                    {item.status !== 'uploading' && item.status !== 'done' ? (
                      <button
                        type="button"
                        className="workouts-queue-remove"
                        onClick={() => removeFromQueue(item.key)}
                        title={t('workouts.upload.remove')}
                        aria-label={`${t('workouts.upload.remove')}: ${item.file.name}`}
                      >
                        ×
                      </button>
                    ) : (
                      <span className="workouts-queue-remove" aria-hidden="true" />
                    )}
                  </li>
                ))}
              </ol>
            )}

            {queue.length > 0 && (
              <div className="workouts-queue-actions">
                <button
                  type="button"
                  className="workouts-primary-btn"
                  onClick={handleUpload}
                  disabled={uploading || pendingCount === 0}
                >
                  {uploading ? t('workouts.upload.uploading') : `${t('workouts.upload.submit')} (${pendingCount})`}
                </button>
                {!uploading && (
                  <button type="button" className="workouts-secondary-btn" onClick={clearQueue}>
                    {t('workouts.upload.clear')}
                  </button>
                )}
              </div>
            )}
            {movedNotice && tree.byId.has(movedNotice.folderId) && (
              <p className="workouts-success workouts-moved-notice">
                {t('workouts.upload.movedToOutdoor')
                  .replace('{{count}}', movedNotice.count)
                  .replace('{{folder}}', tree.byId.get(movedNotice.folderId).name)}
                {currentId !== movedNotice.folderId && (
                  <button type="button" className="workouts-link-btn" onClick={() => selectFolder(movedNotice.folderId)}>
                    {t('workouts.upload.openFolder')}
                  </button>
                )}
              </p>
            )}
          </section>

          {loadError && <p className="workouts-errors">{loadError}</p>}

          {subfolders.length > 0 && (
            <div className="wf-subfolders">
              {subfolders.map((folder) => (
                <div
                  key={folder.id}
                  role="button"
                  tabIndex={0}
                  className={`wf-subfolder ${dropTarget === folder.id && canDrop(folder.id) ? 'wf-node--drop' : ''}`}
                  draggable
                  onDragStart={(event) => dnd.startFolder(event, folder.id)}
                  onDragEnd={endDrag}
                  onClick={() => selectFolder(folder.id)}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectFolder(folder.id); } }}
                  {...dnd.dropProps(folder.id)}
                >
                  <FolderIcon />
                  <span className="wf-subfolder-name">{folder.name}</span>
                  <span className="wf-node-count">{tree.totals.get(folder.id)}</span>
                </div>
              ))}
            </div>
          )}

          {loading && <p className="workouts-muted">{t('common.loading')}</p>}
          {empty && !loadError && (
            <p className="workouts-empty">
              {current ? t('workouts.folders.empty') : t('workouts.empty')}
            </p>
          )}

          <div className="workouts-list">
            {!loading && workouts.map((workout) => {
              const activity = workout.kind === 'activity';
              const metrics = activity ? null : workoutMetrics(workout.segments);
              const summary = workout.summary || {};
              const open = () => navigate(`/workouts/${workout.id}`);
              return (
                <div
                  key={workout.id}
                  role="button"
                  tabIndex={0}
                  className="workout-card"
                  draggable
                  onDragStart={(event) => startWorkoutDrag(event, workout)}
                  onDragEnd={endDrag}
                  onClick={open}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); } }}
                >
                  <div className="workout-card-head">
                    <span className="workout-card-name">{workout.name}</span>
                    <span className="workout-format-badge">
                      {activity ? activityBadge(workout, t) : workout.source_format.toUpperCase()}
                    </span>
                  </div>
                  {activity && workout.started_at && (
                    <span className="workout-card-date">{formatDate(workout.started_at, language)}</span>
                  )}
                  <WorkoutSharedBy sender={workout.shared_by} className="workout-card-shared" />
                  {activity ? (
                    <>
                      {(summary.preview || []).length > 1 && <TrackPreview points={summary.preview} />}
                      <div className="workout-card-stats">
                        {summary.distance_m != null && (
                          <span>{formatDistance(summary.distance_m)} {t('workouts.activity.units.km')}</span>
                        )}
                        {/* У маршрута без времени длительности нет */}
                        {workout.duration_seconds > 0 && <span>{formatDuration(workout.duration_seconds)}</span>}
                        {summary.elevation_gain_m != null && (
                          <span>↑ {summary.elevation_gain_m} {t('workouts.activity.units.m')}</span>
                        )}
                        {summary.avg_power != null && <span>{summary.avg_power} {t('workouts.units.watts')}</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <WorkoutChart segments={workout.segments} duration={workout.duration_seconds} compact />
                      <div className="workout-card-stats">
                        <span>{formatDuration(workout.duration_seconds)}</span>
                        {metrics.tss !== null && <span>TSS {Math.round(metrics.tss)}</span>}
                        {metrics.intensity !== null && <span>IF {metrics.intensity.toFixed(2)}</span>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutsPage;
